import type { Theme } from '@mui/material'
import React, { forwardRef, type ReactElement, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Slide from '@mui/material/Slide'
import { useTheme } from '@mui/material/styles'
import { type TransitionProps } from '@mui/material/transitions'
import useMediaQuery from '@mui/material/useMediaQuery'
import KNForm from 'components/KNForm/KNForm'
import type { KNDialogProps } from './types'

const UpSlideTransition = forwardRef((props: TransitionProps & { children: ReactElement }, ref: Ref<unknown>) => (
  <Slide direction="up" ref={ref} {...props} />
))

const LeftSlideTransition = forwardRef((props: TransitionProps & { children: ReactElement }, ref: Ref<unknown>) => (
  <Slide direction="left" ref={ref} {...props} />
))

const getSlideTransition = (direction?: 'up' | 'left') => {
  switch (direction) {
    case 'up':
      return UpSlideTransition
    case 'left':
      return LeftSlideTransition
    default:
      return undefined
  }
}

const KNDialog = ({
  open,
  onClose,
  title,
  children,
  actions,
  closeAction = true,
  closeLabel,
  allowBackdropClose = false,
  preventClosing,
  maxWidth,
  slideDirection,
  onSubmit,
  sx,
}: KNDialogProps): ReactElement => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()

  const handleClose = (event: React.MouseEvent, reason: string): void => {
    if (preventClosing) {
      return
    }
    if (reason && reason === 'backdropClick' && !allowBackdropClose) {
      return
    }
    onClose?.()
  }

  const baseElement = (
    <>
      {title && (
        <DialogTitle sx={{ flex: 0 }}>
          {title}
        </DialogTitle>
      )}
      <DialogContent>
        {children}
      </DialogContent>
      {(closeAction || actions) && (
        <DialogActions
          sx={{
            flex: 0,
            '& > *': {
              flex: { xs: 1, lg: 'none' },
            },
          }}
        >
          {closeAction && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={(event): void => handleClose(event, 'buttonClick')}
            >
              {closeLabel ?? t('general.close')}
            </Button>
          )}
          {actions}
        </DialogActions>
      )}
    </>
  )

  return (
    <Dialog
      maxWidth={isMobile ? false : maxWidth ?? 'sm'}
      open={open}
      onClose={handleClose}
      slots={{
        transition: getSlideTransition(isMobile ? 'up' : slideDirection),
      }}
      slotProps={{
        paper: {
          sx: (theme: Theme) => ({
            width: '100%',
            overflow: 'hidden',
            ...(isMobile && {
              position: 'fixed',
              bottom: 0,
              margin: 0,
              maxWidth: '100%',
              maxHeight: '75vh',
              borderRadius: `${theme.borders.borderRadius['lg']} ${theme.borders.borderRadius['lg']} 0 0`,
            }),
          }),
        },
      }}
      sx={sx}
    >
      {onSubmit ? (
        <KNForm
          onSubmit={onSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {baseElement}
        </KNForm>
      ) : baseElement}
    </Dialog>
  )
}

export default KNDialog
